import { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { cn } from '@/lib/utils';
import { SITE_TITLE } from '@/lib/site-meta';
import { useThemeCustomizer } from '@/lib/theme-customizer';
import brandLogoBlue from '@/assets/logo/inersia-bleu.png';
import brandLogoMark from '@/assets/logo/inersia_logo.svg';
import brandLogoMarkWhite from '@/assets/logo/inersia_logo_mark_white.svg';
import brandLogoWhite from '@/assets/logo/inersia_logo_white.svg';

/** Hero wordmark — même gabarit que le panneau auth (aside). */
export const BRAND_LOGO_HERO_ASIDE_CLASSNAME =
  'mb-0 max-w-[min(100%,27rem)] md:mb-0 md:max-w-[min(100%,32rem)]';

/** Wordmark compact pour header de conversation — proportionnel au hero aside. */
export const BRAND_LOGO_COMPACT_HEADER_CLASSNAME =
  'mb-0 w-[min(100%,8rem)] max-w-full md:w-[min(100%,9.5rem)]';

type AnimatedBrandLogoVariant = 'hero' | 'compact' | 'symbol';

type AnimatedBrandLogoAnimationMode = 'reveal' | 'loop';

/**
 * - `auto` : logo blanc en dark mode, logo couleur en light mode
 * - `on-dark` : toujours le logo blanc (fond sombre forcé, ex. aside auth)
 * - `on-light` : toujours le logo couleur
 */
type AnimatedBrandLogoAppearance = 'auto' | 'on-dark' | 'on-light';

type AnimatedBrandLogoProps = {
  variant?: AnimatedBrandLogoVariant;
  /** `reveal` : fade-in fluide (hero). `loop` : pas de fade d’entrée. */
  animationMode?: AnimatedBrandLogoAnimationMode;
  /** Taille réduite pour la sidebar repliée. */
  compact?: boolean;
  className?: string;
  /**
   * Variante d’asset selon le fond / le thème.
   * @default 'auto'
   */
  appearance?: AnimatedBrandLogoAppearance;
  /** Rejoue le fade + callback quand la valeur change (intent welcome, conversation, etc.). */
  replayKey?: string;
  /** Appelé après le fade-in (ou immédiatement si reduced-motion / pas de reveal). */
  onRevealComplete?: () => void;
};

const HERO_FADE_DURATION = 0.85;
const HERO_FADE_EASE = 'power2.out';

function resolveBrandLogoSrc(
  appearance: AnimatedBrandLogoAppearance,
  effectiveMode: 'light' | 'dark',
  options?: { compact?: boolean; variant?: AnimatedBrandLogoVariant },
): string {
  // Sidebar repliée : pictogramme Inersia (couleur / blanc selon le thème).
  if (options?.variant === 'symbol' && options.compact) {
    const useWhiteMark =
      appearance === 'on-dark'
      || (appearance === 'auto' && effectiveMode === 'dark');

    return useWhiteMark ? brandLogoMarkWhite : brandLogoMark;
  }

  if (appearance === 'on-dark') {
    return brandLogoWhite;
  }

  if (appearance === 'on-light') {
    return brandLogoBlue;
  }

  return effectiveMode === 'dark' ? brandLogoWhite : brandLogoBlue;
}

type BrandLogoImageProps = {
  isSymbol: boolean;
  src: string;
  replayKey?: string;
  onReady: () => void;
};

function BrandLogoImage({ isSymbol, src, replayKey, onReady }: BrandLogoImageProps) {
  const didNotifyRef = useRef(false);

  const notifyReady = () => {
    if (didNotifyRef.current) {
      return;
    }

    didNotifyRef.current = true;
    onReady();
  };

  return (
    <img
      key={replayKey ?? 'brand-logo'}
      src={src}
      alt={isSymbol ? '' : SITE_TITLE}
      aria-hidden={isSymbol ? true : undefined}
      className={cn(
        'block h-full w-auto max-w-full object-contain',
        isSymbol ? 'object-center' : 'object-left w-full',
      )}
      decoding="async"
      draggable={false}
      ref={(node) => {
        if (node?.complete) {
          notifyReady();
        }
      }}
      onLoad={notifyReady}
    />
  );
}

export function AnimatedBrandLogo({
  variant = 'hero',
  animationMode = 'reveal',
  compact = false,
  className,
  appearance = 'auto',
  replayKey,
  onRevealComplete,
}: AnimatedBrandLogoProps) {
  const { effectiveMode } = useThemeCustomizer();
  const rootRef = useRef<HTMLDivElement>(null);
  const fadeTweenRef = useRef<gsap.core.Tween | null>(null);
  const onRevealCompleteRef = useRef(onRevealComplete);
  const [imageReady, setImageReady] = useState(false);
  const [prevReplayToken, setPrevReplayToken] = useState<string | null>(null);
  const isSymbol = variant === 'symbol';
  const logoSrc = resolveBrandLogoSrc(appearance, effectiveMode, { compact, variant });
  const replayToken = `${replayKey ?? 'brand-logo'}:${logoSrc}`;
  const shouldFadeIn = variant === 'hero' && animationMode === 'reveal';

  if (prevReplayToken !== replayToken) {
    setPrevReplayToken(replayToken);
    setImageReady(false);
  }

  useLayoutEffect(() => {
    onRevealCompleteRef.current = onRevealComplete;
  });

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }

    fadeTweenRef.current?.kill();
    fadeTweenRef.current = null;

    if (!shouldFadeIn) {
      gsap.set(root, { clearProps: 'opacity' });
      if (imageReady) {
        onRevealCompleteRef.current?.();
      }
      return;
    }

    gsap.set(root, { opacity: 0 });

    if (!imageReady) {
      return;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      gsap.set(root, { opacity: 1 });
      onRevealCompleteRef.current?.();
      return;
    }

    fadeTweenRef.current = gsap.to(root, {
      opacity: 1,
      duration: HERO_FADE_DURATION,
      ease: HERO_FADE_EASE,
      onComplete: () => {
        fadeTweenRef.current = null;
        onRevealCompleteRef.current?.();
      },
    });

    return () => {
      fadeTweenRef.current?.kill();
      fadeTweenRef.current = null;
    };
  }, [imageReady, replayToken, shouldFadeIn]);

  const testId =
    variant === 'hero'
      ? 'animated-brand-logo-hero'
      : variant === 'compact'
        ? 'animated-brand-logo-compact'
        : 'animated-brand-logo-symbol';

  return (
    <div
      ref={rootRef}
      className={cn(
        'isolate shrink-0',
        variant === 'hero' && cn('w-full', BRAND_LOGO_HERO_ASIDE_CLASSNAME),
        variant === 'compact' && cn('max-w-full', BRAND_LOGO_COMPACT_HEADER_CLASSNAME),
        variant === 'symbol' && (compact ? 'h-7 w-7 max-w-7' : 'h-8 w-full max-w-full'),
        className,
      )}
      data-testid={testId}
      style={shouldFadeIn ? { opacity: 0 } : undefined}
    >
      <BrandLogoImage
        isSymbol={isSymbol}
        src={logoSrc}
        replayKey={replayToken}
        onReady={() => setImageReady(true)}
      />
    </div>
  );
}
